import { useMemo, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PHASE_COLORS, PHASE_LABEL, PHASE_ORDER } from "./offer-phase-tokens";
import type { OfferLineTemplate } from "@/hooks/use-offers";
import type { OfferPhase } from "@/lib/offer-calc";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templates: OfferLineTemplate[];
  /**
   * Når satt: vis kun maler for den fasen, og skjul fase-grupperingen
   * (overskriften vises fortsatt). Brukes av mersalg-dialogen der PL
   * legger til en linje i en spesifikk fase.
   */
  restrictToPhase?: OfferPhase;
  /** Når admin velger en oppføring. qty parses fra "navn 8" og kan være null. */
  onPick: (
    template: OfferLineTemplate,
    qty: number | null,
  ) => void | Promise<void>;
}

/**
 * Fullskjerms-dialog for å legge til en ny linje fra katalogen. Speiler
 * mønsteret i den globale CommandPalette: stor input øverst, gruppert
 * resultatliste, Enter for å velge.
 *
 * Inputen tolker "navn 8" som template-navn + mengde i én operasjon —
 * samme som det inline-søket i headeren.
 *
 * Brukes både i tilbudseditoren (alle faser) og i mersalg-dialogen
 * (restrictToPhase låser til én fase).
 */
export function OfferCatalogDialog({
  open,
  onOpenChange,
  templates,
  restrictToPhase,
  onPick,
}: Props) {
  const [q, setQ] = useState("");

  const { search, qty } = useMemo(() => {
    const m = q.trim().match(/^(.*?)(?:\s+(\d+(?:[.,]\d+)?))?$/);
    return {
      search: (m?.[1] ?? "").toLowerCase().trim(),
      qty: m?.[2] ? parseFloat(m[2].replace(",", ".")) : null,
    };
  }, [q]);

  const grouped = useMemo(() => {
    const filtered = templates.filter((t) => {
      if (restrictToPhase && t.phase !== restrictToPhase) return false;
      if (search && !t.name.toLowerCase().includes(search)) return false;
      return true;
    });
    const map: Record<string, OfferLineTemplate[]> = {};
    for (const t of filtered) {
      const key = t.phase;
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    return map;
  }, [templates, search, restrictToPhase]);

  async function handlePick(t: OfferLineTemplate) {
    // Vent på opprettelse før dialogen lukker — sikrer at linjen er
    // synlig i tabellen i samme paint-pass og at testflyt ser den uten
    // ekstra ventetider. Skjuler også et tidlig vindu der admin kunne
    // klikke flere ganger og opprette duplikater.
    try {
      await onPick(t, qty);
    } finally {
      setQ("");
      onOpenChange(false);
    }
  }

  const phasesToRender = restrictToPhase ? [restrictToPhase] : PHASE_ORDER;

  // VIB-2096: cmdk sitt innebygde filter matchet rå input («klipp 4») mot
  // malnavnet og ga alltid «Ingen treff» så snart et tall ble skrevet. Vi
  // komponerer Dialog + Command selv (CommandDialog i ui/ sender ikke
  // shouldFilter videre) og lar `grouped` over være eneste filter.
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setQ("");
      }}
    >
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <DialogTitle className="sr-only">
          Legg til linje fra katalog
        </DialogTitle>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder='Søk i katalog (eller skriv f.eks. "klipp 8")...'
            value={q}
            onValueChange={setQ}
          />
          <CommandList>
            <CommandEmpty>Ingen treff i katalogen.</CommandEmpty>
            {phasesToRender.map((phase) => {
              const items = grouped[phase];
              if (!items || items.length === 0) return null;
              const color = PHASE_COLORS[phase];
              return (
                <CommandGroup key={phase} heading={PHASE_LABEL[phase]}>
                  {items.map((t) => {
                    const isExternal = t.cost_type === "external";
                    return (
                      <CommandItem
                        key={t.id}
                        value={`${t.name} ${t.phase} ${isExternal ? "ekstern" : "intern"}`}
                        onSelect={() => handlePick(t)}
                        className="flex items-center gap-3"
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: color }}
                          aria-hidden
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium truncate">
                            {t.name}
                          </span>
                          <span className="block text-[10px] text-muted-foreground">
                            {isExternal ? "Ekstern" : "Intern"}
                            {t.default_unit ? ` · ${t.default_unit}` : ""}
                          </span>
                        </span>
                        {qty !== null && (
                          <span
                            className={cn(
                              "text-xs font-semibold tabular-nums opacity-90",
                            )}
                          >
                            → {qty} {t.default_unit}
                          </span>
                        )}
                        {t.default_rate ? (
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {formatNOK(t.default_rate)}/{t.default_unit}
                          </span>
                        ) : null}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
